import {
    getAttributeColumns,
    getAxisConfig,
    getCategoryColumns,
    getChartType,
    getColumns,
    getLegendColumns,
    getXAxes,
    getYAxes,
    isCustomOrderDefined,
    isExcludeNullValuesSupported,
    isXAxisColumn,
    isYAxisColumn,
} from '@thoughtspot/chart-lib/lib/core/chart-model-utils';
import {
    configOptions,
    isCustomOrTsHostedChart,
    validateAxisConfig,
} from '@thoughtspot/chart-lib/lib/core/chart-type-specification-service';
import { getColumnIdToDisambiguatedName } from '@thoughtspot/chart-lib/lib/core/chart-ui-utils';
import {
    filterMeasureColumnsByChart,
    getOverrideNamesForChartSpecificColumns,
    isMeasureNameOrValueColumn,
} from '@thoughtspot/chart-lib/lib/core/measure-names-and-values';
import { updateColumnsOrderAndVisibility } from '@thoughtspot/chart-lib/lib/core/table-client-state-utils';
import {
    getOrderedHiddenTableColumns,
    getOrderedTableColumns,
    getOrderedVisibleTableColumns,
} from '@thoughtspot/chart-lib/lib/core/table-utils';
import { getTooltipConfig } from '@thoughtspot/chart-lib/lib/data/data-util';
import { DEFAULT_CHART_VIZ_PROPERTIES } from '@thoughtspot/client-state-util';
import { translate } from '@thoughtspot/i18n';
import {
    MetricsService,
    UserAction,
    userActionTypeEnum,
    userActionTypes,
} from '@thoughtspot/metrics';
import { OptionsGroup } from '@thoughtspot/radiant-react/widgets/input-mentions/input-mentions';
import {
    isHandleMissingValuesEnabled,
    isHighchartCustomisationEnabled,
} from '@thoughtspot/session-service';
import {
    ChartConfigEditorDefinition,
    ChartConfigParameters,
} from '@thoughtspot/ts-chart-sdk';
import {
    getId,
    isAttribute,
    isDateColumn,
    isEffectivelyNonNumeric,
    isEffectivelyNumeric,
    isNumeric,
    supportsDateBucketizationChange,
} from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { DraggableId, DraggableLocation } from 'react-beautiful-dnd';
import {
    AnswerColumn,
    AnswerColumnEffectiveType,
    AxisConfig,
    AxisObj,
    ChartColumnPropertyMap,
    ChartViz,
    ChartVizProps,
    TableViz,
    TableVizProps,
    VizColumn,
} from '/@services/generated/graphql-types';
import { getAxisConfigInputFromAxisConfig } from '/@services/visualization/chart/chart-service.util';
import {
    reuseAxesIfPossible,
    reuseChartProperties,
    reuseTooltipConfig,
    unlinkAllInheritance,
} from '/@utils/viz/client-state.util';
import {
    extractAndMatchDynamicTitle,
    SectionalDynamicTitlesType,
} from '/@utils/viz/dynamic-title.util';
import { isDual, isFlat, isMerged } from './core/types/axis-driven.types';
import { EventCollectors } from './use-apply-reset-section';

export enum sectionIds {
    x = 'x',
    y = 'y',
    color = 'color',
    size = 'size',
    category = 'category',
    notVisualized = 'notVisualized',
    columns = 'columns',
    visibleTableColumns = 'visibleTableColumns',
    hiddenTableColumns = 'hiddenTableColumns',
}

/**
 * Axis item types (data structure types)
 */
export enum AxisItemType {
    /** Single flat item */
    FLAT = 'flat',

    /** Merged/grouped item */
    MERGED = 'merged',

    /** Dual-axis comparison item */
    DUAL = 'dual',
}

// Flat axis item - represents a single column
export interface FlatAxisItem {
    id: string;
    type: typeof AxisItemType.FLAT;
    column: AnswerColumn;
}

// Merged axis item - represents multiple columns grouped together
export interface MergedAxisItem {
    id: string;
    type: typeof AxisItemType.MERGED;
    title: string;
    children: FlatAxisItem[];
}

// Dual axis item - represents compared axes (primary vs secondary)
export interface DualAxisItem {
    id: string;
    type: typeof AxisItemType.DUAL;
    title: string;
    primary: MergedAxisItem;
    secondary: MergedAxisItem;
}

// Union type for all axis item types
export type AxisItem = FlatAxisItem | MergedAxisItem | DualAxisItem;

export interface ColumnConfigSectionItem {
    id: string;
    columns: AnswerColumn[] | null;
    name: string;
    actionItemComponent?: () => React.ReactElement;
    // For axis-driven configs, this will contain the axis structure
    items?: AxisItem[];
    // Flag to indicate if this is an axis-driven config
    isAxisDriven?: boolean;
}

export type SectionColumnMap = { [key in sectionIds]: AnswerColumn[] };

export interface SectionColumnChangeInfo {
    draggedColumn: AnswerColumn;
    from: {
        index: number;
        sectionId: string;
    };
    to: {
        index: number;
        sectionId: string;
    };
}

// These selectors are basically functions of chartType. We used to set these properties in
// chartTypeSpecificationService.

export const getColumnsWithDisambiguatedNames = (
    sections: ColumnConfigSectionItem[],
    // chartModel is options and is only used to derive optional override names
    // for chart specific columns
    chartModel?: ChartViz,
    chartConfigParameters?: ChartConfigParameters,
) => {
    const answerCols: AnswerColumn[] = _.compact(
        sections.map(section => section.columns).flat(),
    );
    const uniqueNames = getColumnIdToDisambiguatedName(
        answerCols,
        true,
        translate,
        chartModel
            ? getOverrideNamesForChartSpecificColumns(
                  chartModel,
                  chartConfigParameters,
              )
            : {},
    );
    return uniqueNames;
};

export const getColumnsWithDynamicTitles = (
    sections: ColumnConfigSectionItem[],
    optionsGroup: OptionsGroup[],
) => {
    // Flatten and compact the columns from the sections
    const answerCols: AnswerColumn[] = _.compact(
        sections.map(section => section.columns).flat(),
    );

    // Create a mapping from column ID to its dynamic title
    const dynamicTitleMapping: SectionalDynamicTitlesType = {};

    answerCols.forEach(column => {
        const columnId = getId(column);
        const thisColumnDynamicTitle = column.dynamicTitle;
        const val = extractAndMatchDynamicTitle(
            thisColumnDynamicTitle,
            optionsGroup,
        );
        dynamicTitleMapping[columnId] = val;
    });

    return dynamicTitleMapping;
};
export const isSizeSelectorEnabled = (chartViz: ChartViz): boolean => {
    const options = configOptions(chartViz.config.chartType, chartViz);
    return !!options.radialEnabled;
};

export const isColorSelectorEnabled = (chartViz: ChartViz): boolean => {
    const options = configOptions(chartViz.config.chartType, chartViz);

    return !!options.enableLegendSelector;
};

export const isCategorySelectorEnabled = (chartViz: ChartViz): boolean => {
    const options = configOptions(chartViz.config.chartType, chartViz);
    return !!options.categoryAxisEnabled;
};

const getAxesAlias = (chartModel: ChartViz) => {
    const options = configOptions(chartModel.config.chartType, chartModel);
    return options.axesAlias;
};

export const getTableOrderedSections = (
    tableViz: TableViz,
    isHiddenTableColumnsEnabled: boolean,
): ColumnConfigSectionItem[] => {
    if (!tableViz) {
        return [];
    }
    if (!isHiddenTableColumnsEnabled) {
        const orderedTableColumns = getOrderedTableColumns(tableViz);
        return [
            {
                id: sectionIds.columns,
                columns: orderedTableColumns,
                name: translate('Columns'),
            },
        ];
    }
    const visibleColumns = getOrderedVisibleTableColumns(tableViz);
    const hiddenColumns = getOrderedHiddenTableColumns(tableViz);
    return [
        {
            id: sectionIds.visibleTableColumns,
            columns: visibleColumns,
            name: translate('tables.tableEditor.visibleColumns'),
        },
        {
            id: sectionIds.hiddenTableColumns,
            columns: hiddenColumns,
            name: translate('tables.tableEditor.hiddenColumns'),
        },
    ];
};

export const getOrderedSections = (
    chartViz: ChartViz,
    getEditAxisCtrl: (axes: AxisObj[]) => React.ReactElement,
): ColumnConfigSectionItem[] => {
    const axisConfig: AxisConfig = getAxisConfig(chartViz);
    const chartType = chartViz.config.chartType;
    const allColumns: AnswerColumn[] = filterMeasureColumnsByChart(chartViz);
    const chartConfigOptions = configOptions(chartType, null);

    const isXAndYTransposed =
        chartConfigOptions.allowedConfigurations.transposeXY;
    const axesAlias = getAxesAlias(chartViz);
    const xSection: ColumnConfigSectionItem = {
        id: sectionIds.x,
        columns: axisConfig.x,
        name: translate(
            isXAndYTransposed
                ? (axesAlias.yAxis as any)
                : (axesAlias.xAxis as any),
        ),
        actionItemComponent: () => getEditAxisCtrl(getXAxes(chartViz)),
    };
    const ySection: ColumnConfigSectionItem = {
        id: sectionIds.y,
        columns: axisConfig.y,
        name: translate(
            isXAndYTransposed
                ? (axesAlias.xAxis as any)
                : (axesAlias.yAxis as any),
        ),
        actionItemComponent: () => getEditAxisCtrl(getYAxes(chartViz)),
    };
    const sections: ColumnConfigSectionItem[] = [
        isXAndYTransposed ? ySection : xSection,
        isXAndYTransposed ? xSection : ySection,
    ];
    if (isCategorySelectorEnabled(chartViz)) {
        sections.push({
            id: sectionIds.category,
            columns: axisConfig.category,
            name: translate(axesAlias.category as any),
        });
    }

    if (isColorSelectorEnabled(chartViz)) {
        sections.push({
            id: sectionIds.color,
            columns: axisConfig.color,
            name: translate(axesAlias.legend as any),
        });
    }

    if (isSizeSelectorEnabled(chartViz)) {
        sections.push({
            id: sectionIds.size,
            // size is always just one column, so we cast it to maintain simplicity in ui
            columns: [axisConfig.size],
            name: translate(axesAlias.radial as any),
        });
    }

    // Can prism handle notVisualized also?
    const allVisualizedColumns = [
        ...(axisConfig.x || []).map(col => col && col.id),
        ...(axisConfig.y || []).map(col => col && col.id),
        ...(axisConfig.color || []).map(col => col && col.id),
        axisConfig.size && axisConfig.size.id,
        ...(axisConfig.category || []).map(col => col && col.id),
    ].filter(_.identity);

    sections.push({
        id: sectionIds.notVisualized,
        columns: allColumns.filter(
            column => !allVisualizedColumns.includes(column.id),
        ),
        name: translate('tokenPanel.NotVisualized'),
    });

    return sections;
};

export const getReducedSectionConfig = (
    sections: ColumnConfigSectionItem[],
): AxisConfig | SectionColumnMap =>
    sections.reduce(
        (acc: Record<string, any>, value: ColumnConfigSectionItem) => {
            if (value.id === sectionIds.size) {
                acc[value.id] =
                    value.columns?.length === 1 ? value.columns[0] : null;
                return acc;
            }
            if (value.id !== sectionIds.notVisualized) {
                acc[value.id] = value.columns ? value.columns : [];
            }
            return acc;
        },
        {},
    );

export const hasAxisConfigChanged = (
    newAxisConfig: AxisConfig,
    olderAxisConfig: AxisConfig,
) => {
    return !_.isEqual(
        getAxisConfigInputFromAxisConfig([newAxisConfig]),
        getAxisConfigInputFromAxisConfig([olderAxisConfig]),
    );
};

export const getColumnChoicesForEachAxis = (chartModel: ChartViz) => {
    const options = configOptions(getChartType(chartModel), chartModel);
    const newColumns = getColumns(chartModel);

    const xAxisColumnChoices = options.noMeasuresOnXAxis
        ? getAttributeColumns(chartModel)
        : newColumns;

    const yAxisColumnChoices = newColumns.filter((column: AnswerColumn) => {
        if (options.attributesOnYAxis && isEffectivelyNonNumeric(column)) {
            return true;
        }
        if (!options.noMeasuresOnYAxis && isEffectivelyNumeric(column)) {
            return true;
        }
        return false;
    });

    const colorColumnChoices = newColumns.filter((column: AnswerColumn) => {
        return isAttribute(column);
    });

    const sizeColumnChoices = newColumns.filter((column: AnswerColumn) => {
        return isEffectivelyNumeric(column);
    });

    const categoryColumnChoices = newColumns.filter((column: AnswerColumn) =>
        isAttribute(column),
    );

    return {
        [sectionIds.x]: xAxisColumnChoices,
        [sectionIds.y]: yAxisColumnChoices,
        [sectionIds.color]: colorColumnChoices,
        [sectionIds.size]: sizeColumnChoices,
        [sectionIds.category]: categoryColumnChoices,
    };
};

export const canMoveColumnToSection = (
    chartViz: ChartViz,
    targetColumnSection: string,
    sourceColumn: AnswerColumn,
) => {
    if (targetColumnSection === 'notVisualized') {
        return true;
    }
    const columnsChoices = getColumnChoicesForEachAxis(chartViz);
    if (
        !columnsChoices ||
        !columnsChoices[targetColumnSection as keyof typeof columnsChoices]
    ) {
        return false;
    }
    const isColumnMovable = columnsChoices[
        targetColumnSection as keyof typeof columnsChoices
    ].find((column: AnswerColumn) => {
        return column.id === sourceColumn.id;
    });

    return !!isColumnMovable;
};

const backfillMissingColumnProperties = (
    columns: VizColumn[],
    columnProps: ChartColumnPropertyMap[],
): ChartColumnPropertyMap[] => {
    let columnProperties = columnProps;
    columns.forEach(col => {
        let found = false;
        for (let index = 0; index < columnProperties.length; index++) {
            const colProp = columnProperties[index];
            if (colProp.columnId === col.column.id) {
                found = true;
                break;
            }
        }
        if (!found) {
            columnProperties = [
                ...(columnProperties as any),
                {
                    columnId: col.column.id,
                    columnProperty: {
                        ...DEFAULT_CHART_VIZ_PROPERTIES,
                    },
                },
            ];
        }
    });
    return columnProperties;
};

export const getUpdatedClientStateAfterAxisChange = (
    axisConfig: AxisConfig,
    chartViz: ChartViz,
): ChartVizProps => {
    const existingAxisConfig = getAxisConfig(chartViz);
    const oldVizProp = chartViz.vizProp;
    // added same chart type in place of old and new chart type because for while axis config change old and new chart type will be same
    const reuseAxisProperties = reuseAxesIfPossible(
        chartViz,
        axisConfig,
        chartViz.config.chartType,
        chartViz.config.chartType,
    );
    const reuseChartProps = reuseChartProperties(
        chartViz,
        oldVizProp,
        axisConfig,
    );
    const updatedTooltipConfig = reuseTooltipConfig(
        getTooltipConfig(chartViz),
        axisConfig,
        existingAxisConfig,
    );

    let columnProps = oldVizProp.columnProperties || [];
    const { columns } = chartViz;
    if (
        oldVizProp.chartProperties.allLabels &&
        columns.length !== columnProps.length
    ) {
        columnProps = backfillMissingColumnProperties(columns, columnProps);
    }
    let newVizProp: ChartVizProps = {
        ...oldVizProp,
        tooltipConfig: updatedTooltipConfig,
        axisProperties: !_.isEmpty(reuseAxisProperties)
            ? [...reuseAxisProperties]
            : oldVizProp.axisProperties,
        chartProperties: !_.isEmpty(reuseChartProps)
            ? reuseChartProps
            : oldVizProp.chartProperties,
        columnProperties:
            oldVizProp.chartProperties.allLabels === true
                ? columnProps.map(column => {
                      if (_.isNil(column.columnProperty?.dataLabels)) {
                          return {
                              ...column,
                              columnProperty: {
                                  ...column.columnProperty,
                                  dataLabels: true,
                              },
                          };
                      }
                      return column;
                  })
                : columnProps,
    };

    newVizProp = unlinkAllInheritance(newVizProp);
    return newVizProp;
};

/* TODO: update this function's logic once the renaming columns flag is removed
 * Each column would have its name editable which would render this func useless
 */
export const canEditColumn = (chartModel: ChartViz, column: AnswerColumn) => {
    if (isMeasureNameOrValueColumn(column)) {
        return false;
    }

    // allow editing for all Y Columns to allow formatting
    if (column.type === AnswerColumnEffectiveType.Measure) {
        return true;
    }

    const chartConfigOptions = configOptions(chartModel.config.chartType, null);
    const isLegend =
        _.indexOf(
            getLegendColumns(chartModel).map(col => col.id),
            column.id,
        ) >= 0;
    const isCategory =
        _.indexOf(
            getCategoryColumns(chartModel).map(col => col.id),
            column.id,
        ) >= 0;
    const configureColorForXAttrs =
        chartConfigOptions.allowedConfigurations.configureColorForXAttrs;
    const allowColorChange =
        chartConfigOptions.allowedConfigurations.allowColorChange;
    const isCategoryAxisEnabled = chartConfigOptions.categoryAxisEnabled;

    // disabling this for all custom charts
    // this would be enabled for rename columns feature only
    // will be added as part of BYOC GA epic.
    if (isCustomOrTsHostedChart(getChartType(chartModel))) {
        return false;
    }

    // case for changing the colors for attributes
    if (
        isAttribute(column) &&
        allowColorChange &&
        (isLegend || configureColorForXAttrs)
    ) {
        return true;
    }

    if (isAttribute(column) && isCategory && isCategoryAxisEnabled) {
        return true;
    }
    if (
        isNumeric(column) &&
        isAttribute(column) &&
        isXAxisColumn(chartModel as ChartViz, column.id) &&
        !supportsDateBucketizationChange(column)
    ) {
        return true;
    }
    if (
        chartConfigOptions.allowedConfigurations.allowChartCustomisation &&
        isHighchartCustomisationEnabled()
    ) {
        return true;
    }

    if (
        isAttribute(column) &&
        isYAxisColumn(chartModel as ChartViz, column.id) &&
        !supportsDateBucketizationChange(column) &&
        chartConfigOptions.allowedConfigurations.conditionalFormatting &&
        chartConfigOptions.allowedConfigurations.conditionalFormatEditorParams
            .isConditionalFormattingAllowedOnAttributeColumn
    ) {
        return true;
    }

    // For pivot table attribute value columns should be allowed to have a conditional formatting.
    // Conditional formatting is not required for Date columns.
    if (
        isAttribute(column) &&
        !isDateColumn(column) &&
        isYAxisColumn(chartModel as ChartViz, column.id) &&
        chartConfigOptions.allowedConfigurations.allowAttributesInPivotData &&
        chartConfigOptions.allowedConfigurations.conditionalFormatEditorParams
            .isConditionalFormattingAllowedOnAttributeColumn
    ) {
        return true;
    }

    // Attribute columns which has custom order defined on worksheet should be allowed to change sort order(default/custom)
    if (isAttribute(column) && isCustomOrderDefined(column)) {
        return true;
    }

    if (
        isHandleMissingValuesEnabled() &&
        isDateColumn(column) &&
        isExcludeNullValuesSupported(chartModel, column.id)
    ) {
        return true;
    }
    return false;
};

export const validateChartConfig = (
    axisConfig: AxisConfig,
    chartViz: ChartViz,
): string => {
    const axisConfigValidity = validateAxisConfig(
        chartViz.config.chartType,
        axisConfig,
        chartViz,
        translate,
    );
    if (!axisConfigValidity.isValid && _.isEmpty(axisConfigValidity.errors)) {
        return translate(
            'chart.chartEditor.chartAxisConfigurator.CONFIG_INVALID_FOR_CHART_TYPE',
        );
    }
    return Object.values(axisConfigValidity.errors)
        .filter(_.identity)
        .join(', ');
};

export const validateTableConfig = (config: SectionColumnMap): string => {
    if (config.visibleTableColumns.length === 0) {
        return translate(
            'tableEditorErrorExplanation.ATLEAST_ONE_VISIBLE_COLUMN',
        );
    }
    return null;
};

export const isTableConfigChanged = (
    sectionColMap: SectionColumnMap,
    tableViz: TableViz,
    hiddenColumnsEnabled: boolean,
): boolean => {
    if (!hiddenColumnsEnabled) {
        const tableColumns = getOrderedTableColumns(tableViz);
        return !_.isEqual(tableColumns, sectionColMap.columns);
    }
    const hiddenColumns = tableViz && getOrderedHiddenTableColumns(tableViz);
    const visibleColumns = tableViz && getOrderedVisibleTableColumns(tableViz);

    return (
        !_.isEqual(visibleColumns, sectionColMap.visibleTableColumns) ||
        !_.isEqual(hiddenColumns, sectionColMap.hiddenTableColumns)
    );
};

export const getUpdatedTableVizProps = (
    sectionColMap: SectionColumnMap,
    tableViz: TableViz,
    hiddenColumnsEnabled: boolean,
): TableVizProps => {
    const orderedColumnIds: string[] = [];
    const orderedHiddenColumnsIds: string[] = [];
    if (hiddenColumnsEnabled) {
        orderedColumnIds.push(
            ...sectionColMap.visibleTableColumns.map(col => col.id),
        );
        orderedHiddenColumnsIds.push(
            ...sectionColMap.hiddenTableColumns.map(col => col.id),
        );
        orderedColumnIds.push(...orderedHiddenColumnsIds);
    } else {
        orderedColumnIds.push(...sectionColMap.columns.map(col => col.id));
    }
    return updateColumnsOrderAndVisibility(
        tableViz?.vizProp,
        orderedColumnIds,
        orderedHiddenColumnsIds,
    );
};

export function getUpdateOrderedSections(
    orderedSections: ColumnConfigSectionItem[],
    {
        source,
        destination,
        draggableId,
        draggedCol,
    }: {
        source: DraggableLocation;
        destination: DraggableLocation;
        draggableId: DraggableId;
        draggedCol: AnswerColumn;
    },
) {
    const isDroppedInSameSection =
        source.droppableId === destination.droppableId;
    return orderedSections.map(section => {
        // cast object to make sure we don't mutate same object
        const newSection = {
            ...section,
        };

        // remove column from source
        if (section.id === source.droppableId) {
            newSection.columns = section.columns.filter(
                col => col.id !== draggableId,
            );
        }

        // add column to destination for specified index
        // Note: if dropped in same section then earlier if is already executed and we
        // need to change newSection only. As index might have been changed
        if (section.id === destination.droppableId) {
            // otherwise this gives error. Objects and Array marked non extensible
            const temp = [
                ...(isDroppedInSameSection ? newSection : section).columns,
            ];
            temp.splice(destination.index, 0, draggedCol);
            newSection.columns = [...temp];
        }

        return {
            ...newSection,
        };
    });
}

export const findSection = (
    sections: ColumnConfigSectionItem[],
    columnId: string,
): ColumnConfigSectionItem =>
    sections.find(section => section.columns.some(col => col.id === columnId));

export const findColumn = (
    section: ColumnConfigSectionItem,
    columnId: string,
): AnswerColumn => section.columns.find(col => col.id === columnId);

export const findSectionIndex = (
    sections: ColumnConfigSectionItem[],
    sectionId: string,
): number => sections.findIndex(section => section.id === sectionId);

/**
 * Method to get event collectors
 * @param getAnswerMixpanelProperties
 * @param metricsService
 * @returns event collector callbacks
 */
export const getChartsEventCollectors = (
    getAnswerMixpanelProperties: () => any,
    metricsService: MetricsService,
): EventCollectors => {
    return {
        applyConfig: (showModal: boolean) => {
            const answerCommonProperties = getAnswerMixpanelProperties();
            if (showModal) {
                metricsService.collectEvent(
                    new UserAction(userActionTypeEnum.CHART_CONFIG_MODAL),
                    {
                        ...answerCommonProperties,
                        type: userActionTypes.APPLY_CONFIG_THROUGH_MODAL.name,
                    },
                );
            } else {
                metricsService.collectEvent(
                    new UserAction(userActionTypeEnum.CLICK),
                    {
                        ...answerCommonProperties,
                        trackId: userActionTypes.APPLY_CONFIG_CHANGE.name,
                    },
                );
            }
        },
        resetConfig: (showModal: boolean) => {
            const answerCommonProperties = getAnswerMixpanelProperties();
            if (showModal) {
                metricsService.collectEvent(
                    new UserAction(userActionTypeEnum.CHART_CONFIG_MODAL),
                    {
                        ...answerCommonProperties,
                        type: userActionTypes.RESET_CONFIG_THROUGH_MODAL.name,
                    },
                );
            } else {
                metricsService.collectEvent(
                    new UserAction(userActionTypeEnum.CLICK),
                    {
                        ...answerCommonProperties,
                        trackId: userActionTypes.RESET_CONFIG_CHANGE.name,
                    },
                );
            }
        },
        cancelAction: () => {
            const answerCommonProperties = getAnswerMixpanelProperties();
            metricsService.collectEvent(
                new UserAction(userActionTypeEnum.CHART_CONFIG_MODAL),
                {
                    ...answerCommonProperties,
                    type: userActionTypes.CANCEL_ACTION_MODAL.name,
                },
            );
        },
    };
};

/**
 * Removes a column from its source section and moves it to the "notVisualized" section
 * for axis-driven configurations. Handles flat, merged, and dual item types.
 *
 * @param orderedSections - The current sections configuration
 * @param columnId - The ID of the column to remove
 * @param chartConfigEditorDefinition - Chart configuration definition to check section configs
 * @param typeHelpers - Helper functions to identify item types (isFlat, isMerged, isDual)
 * @returns Updated sections with the column moved to notVisualized, or null if column not found
 */
export const removeColumnFromAxisSections = (
    orderedSections: ColumnConfigSectionItem[],
    columnId: string,
    chartConfigEditorDefinition: ChartConfigEditorDefinition,
): ColumnConfigSectionItem[] | null => {
    // Find the column and get its data
    let column: AnswerColumn | null = null;
    let sourceSectionId: string | null = null;

    // First, find the column to get its data
    orderedSections.some((section: ColumnConfigSectionItem) => {
        return section.items.some((item: AxisItem) => {
            if (isFlat(item)) {
                if (item.column.id === columnId) {
                    column = item.column;
                    sourceSectionId = section.id;
                    return true;
                }
            }
            if (isMerged(item)) {
                const foundChild = item.children.find(
                    (child: FlatAxisItem) => child.column?.id === columnId,
                );
                if (foundChild) {
                    column = foundChild.column;
                    sourceSectionId = section.id;
                    return true;
                }
            }
            if (isDual(item)) {
                const foundInPrimary = item.primary.children.find(
                    (child: FlatAxisItem) => child.column?.id === columnId,
                );
                const foundInSecondary = item.secondary.children.find(
                    (child: FlatAxisItem) => child.column.id === columnId,
                );
                if (foundInPrimary) {
                    column = foundInPrimary.column;
                    sourceSectionId = section.id;
                    return true;
                }
                if (foundInSecondary) {
                    column = foundInSecondary.column;
                    sourceSectionId = section.id;
                    return true;
                }
            }
            return false;
        });
    });

    if (!column || !sourceSectionId) {
        return null;
    }

    // Create new sections with the column removed and added to notVisualized
    const newSections = orderedSections.map(
        (section: ColumnConfigSectionItem) => {
            // Add to notVisualized section
            if (section.id === sectionIds.notVisualized) {
                return {
                    ...section,
                    columns: [...section.columns, column],
                    items: [
                        ...section.items,
                        {
                            id: columnId,
                            title: column.name || columnId,
                            type: AxisItemType.FLAT,
                            column,
                        },
                    ],
                };
            }

            // Remove from source section
            if (section.id === sourceSectionId) {
                // First, update the items array
                const updatedItems = section.items.flatMap((item: any) => {
                    // Remove flat item if it matches
                    if (isFlat(item) && item.column.id === columnId) {
                        return []; // Remove it
                    }

                    // Handle merged items
                    if (isMerged(item)) {
                        const newChildren = item.children.filter(
                            (child: any) => child.column.id !== columnId,
                        );

                        // If no children remain, remove the group entirely
                        if (newChildren.length === 0) {
                            return [];
                        }

                        // Check if we should dissolve the merged group to flat item when 1 child remains
                        const sectionConfig = chartConfigEditorDefinition?.columnSections?.find(
                            (s: { key: string }) => s.key === sourceSectionId,
                        );
                        const allowFlatItems = (sectionConfig as any)
                            ?.allowFlatItems;

                        // If allowFlatItems is false, keep as merged group
                        // If allowFlatItems is true or undefined, dissolve to flat item when 1 child
                        if (
                            newChildren.length === 1 &&
                            allowFlatItems !== false
                        ) {
                            return newChildren; // Dissolve to flat item
                        }

                        // Return updated merged item
                        return [
                            {
                                ...item,
                                children: newChildren,
                            },
                        ];
                    }

                    // Handle dual items
                    if (isDual(item)) {
                        const newPrimaryChildren = item.primary.children.filter(
                            (child: any) => child.column.id !== columnId,
                        );
                        const newSecondaryChildren = item.secondary.children.filter(
                            (child: any) => child.column.id !== columnId,
                        );

                        const primaryEmpty = newPrimaryChildren.length === 0;
                        const secondaryEmpty =
                            newSecondaryChildren.length === 0;

                        // If both sides empty, remove the dual item
                        if (primaryEmpty && secondaryEmpty) {
                            return [];
                        }

                        // If one side is empty, convert to merged item
                        if (primaryEmpty || secondaryEmpty) {
                            const remainingChildren = primaryEmpty
                                ? newSecondaryChildren
                                : newPrimaryChildren;

                            // Check if we should dissolve the merged group to flat item when 1 child remains
                            const sectionConfig = chartConfigEditorDefinition?.columnSections?.find(
                                (s: { key: string }) =>
                                    s.key === sourceSectionId,
                            );
                            const allowFlatItems = (sectionConfig as any)
                                ?.allowFlatItems;

                            // If allowFlatItems is false, keep as merged group
                            // If allowFlatItems is true or undefined, dissolve to flat item when 1 child
                            if (
                                remainingChildren.length === 1 &&
                                allowFlatItems !== false
                            ) {
                                return remainingChildren; // Dissolve to flat item
                            }

                            // Convert to merged item
                            return [
                                {
                                    id: item.id,
                                    title: item.title,
                                    type: AxisItemType.MERGED,
                                    children: remainingChildren,
                                },
                            ];
                        }

                        // Both sides have items, return updated dual item
                        return [
                            {
                                ...item,
                                primary: {
                                    ...item.primary,
                                    children: newPrimaryChildren,
                                },
                                secondary: {
                                    ...item.secondary,
                                    children: newSecondaryChildren,
                                },
                            },
                        ];
                    }

                    return [item];
                });

                // Extract columns from updated items to keep columns array in sync
                const updatedColumns: AnswerColumn[] = [];
                updatedItems.forEach((item: AxisItem) => {
                    if (isFlat(item)) {
                        updatedColumns.push(item.column);
                    } else if (isMerged(item)) {
                        item.children.forEach((child: FlatAxisItem) => {
                            updatedColumns.push(child.column);
                        });
                    } else if (isDual(item)) {
                        item.primary.children.forEach((child: FlatAxisItem) => {
                            updatedColumns.push(child.column);
                        });
                        item.secondary.children.forEach(
                            (child: FlatAxisItem) => {
                                updatedColumns.push(child.column);
                            },
                        );
                    }
                });

                return {
                    ...section,
                    columns: updatedColumns,
                    items: updatedItems,
                };
            }

            return section;
        },
    );

    return newSections;
};
